import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import PlotAccordion from "./components/PlotAccordion";
import { getNewsByWorkId, getRelatedWorks, getWorkById } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const work = getWorkById(id);
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: work
      ? `${work.type === "movie" ? "电影" : "电视剧"}《${work.title}》剧情介绍与解析 - 挣扎吧亚当君`
      : "作品未找到 - 挣扎吧亚当君",
    description: work
      ? `${work.title}（${work.year}，${work.region}）剧情介绍与解析：${work.synopsis}`
      : "抱歉，未找到该影视作品。",
    keywords: work ? `${work.title},${work.title}剧情介绍,${work.title}解析,${work.genres.join(",")}` : "影视作品",
  });

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (!work) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-file-unknow-line text-3xl"></i>
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">链接可能已失效，或作品尚未收录。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const typeLabel = work.type === "movie" ? "电影" : "电视剧";
  const fav = isFavorite(work.id);
  const related = getRelatedWorks(work, 4);
  const relatedNews = getNewsByWorkId(work.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快分享给同好吧");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    const nowFav = toggle(work.id);
    setToast(nowFav ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full">
        <div className="absolute inset-0 h-[420px] md:h-[480px] overflow-hidden">
          <img
            src={work.backdrop}
            alt={`《${work.title}》剧照背景`}
            title={`${work.title} - 影视作品`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-primary-950/70 to-primary-950/60"></div>
        </div>

        <div className="relative px-4 md:px-8 pt-6">
          <div className="[&_*]:text-background-200/80">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: typeLabel, to: work.type === "movie" ? "/category/movie" : "/category/tv" },
                { label: work.title },
              ]}
            />
          </div>

          <div className="mt-8 md:mt-12 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-40 md:w-56 shrink-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/20 bg-secondary-100">
                <img
                  src={work.poster}
                  alt={`${typeLabel}《${work.title}》海报`}
                  title={`${work.title} - 剧情介绍海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 pb-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-accent-950">
                  {typeLabel}
                </span>
                <span className="text-sm text-background-100/80">
                  {work.year} · {work.region} · {work.duration}
                </span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50">{work.title}</h1>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1 text-accent-400 font-bold text-xl">
                  <i className="ri-star-fill text-base"></i>
                  {work.rating.toFixed(1)}
                </span>
                {work.genres.map((g) => (
                  <span
                    key={g}
                    className="px-2.5 py-0.5 rounded-full text-xs bg-background-50/15 text-background-50 backdrop-blur-sm"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm max-w-xl">
                <div className="flex gap-2">
                  <dt className="text-background-200/70 shrink-0">导演</dt>
                  <dd className="text-background-50 font-medium">{work.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-200/70 shrink-0">主演</dt>
                  <dd className="text-background-50 font-medium truncate">{work.cast.join(" / ")}</dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-bold transition-colors whitespace-nowrap cursor-pointer ${
                    fav ? "bg-accent-500 text-accent-950" : "bg-background-50/15 text-background-50 hover:bg-background-50/25"
                  }`}
                >
                  <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {fav ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
                <Link
                  to={work.type === "movie" ? "/category/movie" : "/category/tv"}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md text-background-100/80 text-sm font-medium hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{typeLabel}列表
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-10">
            <section className="bg-background-50 rounded-lg border border-background-200/80 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.synopsis}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {work.tags.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full text-xs bg-accent-100 text-accent-800">
                    {t}
                  </span>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-5">
                <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">剧情导航</h2>
              </div>
              <p className="text-sm text-foreground-500 mb-5">
                点击章节即可展开，按需阅读，保留你想要的悬念。
              </p>
              <PlotAccordion chapters={work.plot} />
            </section>

            {relatedNews.length > 0 && (
              <section>
                <SectionHeading eyebrow="Related" title="相关影视资讯" />
                <div className="space-y-4">
                  {relatedNews.map((n) => (
                    <NewsCard key={n.id} item={n} layout="row" />
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-6">
            <div className="bg-background-50 rounded-lg border border-background-200/80 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">影片资料</h3>
              <dl className="space-y-3 text-sm">
                {[
                  { k: "作品类型", v: typeLabel },
                  { k: "上映年份", v: `${work.year}` },
                  { k: "制片地区", v: work.region },
                  { k: "导演", v: work.director },
                  { k: "时长/集数", v: work.duration },
                  { k: "主演", v: work.cast.join(" / ") },
                ].map((row) => (
                  <div key={row.k} className="flex gap-3">
                    <dt className="text-foreground-500 w-20 shrink-0">{row.k}</dt>
                    <dd className="text-foreground-900 flex-1">{row.v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="bg-primary-950 rounded-lg p-5 text-background-100">
              <h3 className="font-heading text-base font-bold text-background-50 mb-2">想看更多解析？</h3>
              <p className="text-sm text-background-200/80 leading-relaxed">
                进入资讯频道，阅读关于《{work.title}》的深度解读与幕后故事。
              </p>
              <Link
                to="/news"
                className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-accent-950 text-sm font-bold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                前往资讯频道
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <SectionHeading
              eyebrow="You May Also Like"
              title="相关推荐"
              desc="根据类型与导演匹配的同类作品。"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm shadow-none animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}